import { AppSession } from "../features/accounts/account";
import { GA_DEBUG_MODE } from "../features/analytics/analytics";
import apiClient from "./apiClient";
import { getUserConsent } from "./consent";
import { devLog, getCookie, setCookie } from "./util";

export function callGtag<Command extends keyof Gtag.GtagCommands>(
  command: Command,
  ...args: Gtag.GtagCommands[Command]
): void {
  if (!getUserConsent().analytics) return;
  gtag(command, ...args);
}

// Cross-property attribution service for V2 client
export class CrossPropertyTracker {
  /**
   * Get platform context from cookies
   */
  private getPlatformContext(): {
    abGroup?: string;
    loginPreference?: string;
  } {
    return {
      abGroup: getCookie("cm_ab_group") || undefined,
      loginPreference: getCookie("cm_last_login") || undefined,
    };
  }

  /**
   * Get or create Front GA client ID from localStorage/cookie set by Front
   */
  private getFrontClientId(): string {
    // Check localStorage first (set by Front ClientLink or previous V2 session)
    const fromStorage = localStorage.getItem("front_ga_client_id");
    if (fromStorage) return fromStorage;

    // Check cookie as fallback
    const clientIdFromCookie = getCookie("front_ga_client_id");
    if (clientIdFromCookie) {
      // Store in localStorage for faster access next time
      localStorage.setItem("front_ga_client_id", clientIdFromCookie);
      return clientIdFromCookie;
    }

    // Generate client ID if none found (ensures we always send events to Front)
    const generatedClientId =
      Math.random().toString(36).substring(2, 15) +
      Math.random().toString(36).substring(2, 15);

    // Store in localStorage
    localStorage.setItem("front_ga_client_id", generatedClientId);

    // Set cookie for cross-subdomain access
    try {
      setCookie(
        "front_ga_client_id",
        generatedClientId,
        30,
        ".catalogmachine.com"
      );
    } catch (error) {
      devLog(`Failed to set client ID cookie: ${error}`);
    }

    devLog(`🆔 Generated new Front GA client ID: ${generatedClientId}`);
    return generatedClientId;
  }

  async trackEvent(
    session: AppSession,
    eventName:
      | "signup"
      | "session"
      | "subscribe"
      | "change_plan"
      | "cancel_plan"
      | "cancel",
    metadata?: Record<string, unknown>,
    userData?: Record<string, unknown>
  ): Promise<void> {
    if (!getUserConsent().analytics) return;

    // Validate session object
    if (!session?.account?.id || !session?.user?.id) {
      devLog(
        `⚠️ Invalid session object for event '${eventName}': missing account or user data`
      );
      return;
    }

    const frontClientId = this.getFrontClientId(); // Always returns a client ID now
    const platformContext = this.getPlatformContext(); // Get platform routing context

    try {
      // Track to V2 GA (normal tracking) - non-blocking
      try {
        const gtagParams: Record<string, unknown> = {
          debug_mode: GA_DEBUG_MODE,
          ...metadata,
          // Include platform context in gtag events too
          ...(platformContext.abGroup && {
            ab_group: platformContext.abGroup,
          }),
          ...(platformContext.loginPreference && {
            login_preference: platformContext.loginPreference,
          }),
        };
        callGtag("event", eventName, gtagParams);
      } catch (gtagError) {
        devLog(`⚠️ Failed to track to V2 GA: ${gtagError}`);
        // Continue with Front GA tracking even if V2 fails
      }

      var eventData = {
        app: "cm2",
        ...metadata,
        // Include platform context in event data
        ...(platformContext.abGroup && {
          ab_group: platformContext.abGroup,
        }),
        ...(platformContext.loginPreference && {
          login_preference: platformContext.loginPreference,
        }),
      };

      // Always send to Front GA4 - with timeout and retry logic
      try {
        await apiClient.post("/analytics/key-event", {
          eventName,
          frontClientId,
          accountId: session.account.id,
          namespace: session.account.namespace,
          userId: session.user.id,
          metadata: eventData,
          userData: {
            plan: session.account.planCode,
            age: session.account.age,
            ...userData,
          },
          abGroup: platformContext.abGroup,
          loginPreference: platformContext.loginPreference,
        });

        devLog(
          `✅ Event '${eventName}' tracked to both V2 and Front GA with platform context:`,
          platformContext
        );
      } catch (apiError) {
        // Log detailed error but don't throw - analytics failures shouldn't break user experience
        devLog(
          `⚠️ Failed to track event '${eventName}' to Front GA:`,
          apiError
        );
      }
    } catch (error) {
      devLog(`Unexpected error in trackEvent '${eventName}': ${error}`);
    }
  }
}

// Export singleton instance
export const crossPropertyTracker = new CrossPropertyTracker();
